import { DOCUMENT } from '@angular/common';
import { ApplicationRef, Inject, Injectable, Injector, OnDestroy, Type } from '@angular/core';
import { GuardsCheckEnd, Router } from '@angular/router';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { filter, NEVER, Subscription } from 'rxjs';
import { getRouteChain, isSameRouteChain } from './route-chain';

/** PrimeNG attaches every dynamic dialog to the app root, so a dialog outlives the page that opened it.
 * Browser Back while a dialog was open destroyed the page, the page detached the entities the dialog
 * shows, and the dialog's next render threw "Cannot read properties of null".
 *
 * Once the guards have passed for a navigation that replaces the page, this removes the open dialogs
 * before the page tears down. They are destroyed, not closed: a close animates for a few frames against
 * the detached data, and emitting onClose would resume the opener's code on a page that is gone. So the
 * opener's onClose never emits.
 *
 * PrimeNG's open() returns null when a dialog of the same component type is already open. A double
 * click on a button whose handler queries before it opens a dialog runs the handler twice, so the
 * second open got null and its caller threw "Cannot read properties of null (reading 'onClose')".
 * open() here gives that second caller a ref whose onClose never emits, for the same reason as above:
 * the dialog that is already open owns the flow, and the second caller must not resume. */
@Injectable()
export class ProxDialogService extends DialogService implements OnDestroy {
  private navigationSub: Subscription;

  constructor(appRef: ApplicationRef, injector: Injector, @Inject(DOCUMENT) document: Document, router: Router) {
    super(appRef, injector, document);
    this.navigationSub = router.events
      .pipe(filter((e): e is GuardsCheckEnd => e instanceof GuardsCheckEnd && e.shouldActivate))
      .subscribe((e) => {
        const current = getRouteChain(router.routerState.snapshot.root);
        if (!isSameRouteChain(current, getRouteChain(e.state.root))) {
          [...this.dialogComponentRefMap.keys()].forEach((ref) => ref.destroy());
        }
      });
  }

  override open<T>(componentType: Type<T>, config: DynamicDialogConfig): DynamicDialogRef<T> {
    const ref = super.open(componentType, config);
    if (ref) return ref;
    const refused = new DynamicDialogRef<T>();
    refused.onClose = NEVER;
    return refused;
  }

  ngOnDestroy() {
    this.navigationSub.unsubscribe();
  }
}

/** Provide this wherever a module or component would provide PrimeNG's DialogService. */
export const ProxDialogServiceProvider = { provide: DialogService, useClass: ProxDialogService };
